/**
 * Pinned clarifying-question panel (``research_assistant_pinned_clarify``).
 *
 * When the transcript's LAST message carries an unanswered clarifying
 * question, the layouts render this panel directly above the composer —
 * the options sit where the researcher is about to type, instead of
 * scrolling away inside the stream. Options are vertically stacked,
 * numbered rows; choosing one submits the same
 * ``{ text, questionId, choice }`` answer payload the in-stream card sends.
 * "Other…" expands an inline free-text field; typing straight into the
 * composer below also works (the panel is an affordance, not a gate).
 *
 * The question text itself stays in the message stream (it is the agent's
 * ask and must survive in the transcript); this panel repeats it as its
 * header while live, and disappears once answered or superseded.
 */

import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  ButtonBase,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ConceptGroupDetailsDialog } from '@verantos-dev/feature-concept-groups-list';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { Markdown } from './Markdown';
import type { EpiChatSendPayload, EpiClarifyingQuestion } from '../types/epi-chat-types';

/** Composer placeholder while a question is pinned — mirrors the panel's
 *  "pick one, or just type" contract. Plain string by design: the composer's
 *  own default placeholder is a plain string too. */
export const REPLY_DIRECTLY_PLACEHOLDER = 'Or reply directly…';

interface PinnedClarifyingQuestionProps {
  question: EpiClarifyingQuestion;
  onAnswer: (answer: EpiChatSendPayload) => void;
  /** Hide the panel for this question without answering (the researcher can
   *  still reply directly in the composer below). */
  onDismiss: () => void;
  disabled?: boolean;
}

export const PinnedClarifyingQuestion = ({
  question,
  onAnswer,
  onDismiss,
  disabled,
}: PinnedClarifyingQuestionProps) => {
  const intl = useIntl();
  const [customMode, setCustomMode] = useState(false);
  const [customText, setCustomText] = useState('');
  // Drill-in: preview a candidate concept group without leaving the panel.
  const [openConceptGroupId, setOpenConceptGroupId] = useState<string | null>(null);

  const handleOption = (label: string) => {
    onAnswer({ text: label, questionId: question.questionId, choice: label });
  };

  const handleCustomSubmit = () => {
    const trimmed = customText.trim();
    if (!trimmed) return;
    onAnswer({ text: trimmed, questionId: question.questionId });
  };

  return (
    <Paper
      elevation={3}
      data-testid="pinned-clarifying-question"
      sx={{
        marginBottom: 1.5,
        padding: 3,
        borderRadius: `${borderRadius.lg}px`,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Markdown dense>{question.question}</Markdown>
        </Box>
        <Tooltip
          title={intl.formatMessage({
            id: 'epiChat.clarify.dismiss',
            defaultMessage: 'Dismiss — reply in the box below',
          })}
        >
          <IconButton
            size="small"
            aria-label="Dismiss the suggested answers"
            data-testid="pinned-clarify-dismiss"
            onClick={onDismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      <Stack divider={<Divider flexItem />} sx={{ marginTop: 1.5 }}>
        {question.options.map((opt, index) => (
          <Stack key={opt.label} direction="row" alignItems="center">
            <ButtonBase
              onClick={() => handleOption(opt.label)}
              disabled={disabled}
              title={opt.description}
              data-testid={`pinned-clarify-option-${index}`}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                justifyContent: 'flex-start',
                gap: 3,
                paddingX: 1.5,
                paddingY: 2,
                borderRadius: `${borderRadius.sm}px`,
                textAlign: 'left',
                '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
                '&:focus-visible': {
                  outline: (theme) => `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: -2,
                },
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: `${borderRadius.sm}px`,
                  backgroundColor: (theme) => theme.palette.action.selected,
                  color: 'text.secondary',
                  typography: 'caption',
                }}
              >
                {index + 1}
              </Box>
              <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                {opt.label}
              </Typography>
            </ButtonBase>
            {opt.conceptGroupId && (
              <Tooltip title="View this concept group">
                <IconButton
                  size="small"
                  aria-label={`View concept group ${opt.label}`}
                  onClick={() => setOpenConceptGroupId(opt.conceptGroupId ?? null)}
                  sx={{ marginLeft: 0.5, flexShrink: 0 }}
                >
                  <OpenInNewIcon fontSize="inherit" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ))}
        {question.allowCustom && !customMode && (
          <ButtonBase
            onClick={() => setCustomMode(true)}
            disabled={disabled}
            data-testid="pinned-clarify-other"
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 3,
              paddingX: 1.5,
              paddingY: 2,
              borderRadius: `${borderRadius.sm}px`,
              textAlign: 'left',
              color: 'text.secondary',
              '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 16, marginX: 1 }} aria-hidden />
            <Typography variant="body2">
              {intl.formatMessage({
                id: 'epiChat.clarify.other',
                defaultMessage: 'Something else…',
              })}
            </Typography>
          </ButtonBase>
        )}
      </Stack>

      {customMode && (
        <Box sx={{ marginTop: 1.5, display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="Type your answer"
            value={customText}
            onChange={(e) => setCustomText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleCustomSubmit();
              }
            }}
            disabled={disabled}
          />
          <Button
            variant="contained"
            onClick={handleCustomSubmit}
            disabled={disabled || !customText.trim()}
            size="small"
          >
            Send
          </Button>
        </Box>
      )}

      {openConceptGroupId && (
        <ConceptGroupDetailsDialog
          conceptGroupId={openConceptGroupId}
          onClose={() => setOpenConceptGroupId(null)}
        />
      )}
    </Paper>
  );
};
